<template>
  <modal
    :show.sync="internalShow"
    @close="handleClose"
    size="lg"
    :modalClasses="'modal-xl'"
  >
    <template #header>
      <div class="text-xl font-semibold">
        {{
          dualFirmMode
            ? "Service Request Payment"
            : "Invoice Payment"
        }}
      </div>
    </template>
    <div class="invoice-payment-popup">
      <!-- Customer Summary -->
      <div
        class="p-4 bg-gray-100 border-l-4 border-blue-500 rounded-lg customer-summary"
      >
        <h4 class="mb-2 font-bold">Customer Information</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div class="flex flex-col">
            <strong>Customer:</strong>
            <span class="text-blue-600">{{ customerName }}</span>
          </div>
          <div class="flex flex-col">
            <strong>{{ dualFirmMode ? "Service Request:" : "Firm:" }}</strong>
            <span class="text-blue-600">
              {{
                dualFirmMode
                  ? serviceRequestNumber || "—"
                  : firmName
              }}
            </span>
          </div>
          <div class="flex flex-col">
            <strong>{{ dualFirmMode ? "Payable invoices:" : "Total Invoices:" }}</strong>
            <span class="text-blue-600">{{ selectedInvoices.length }}</span>
          </div>
        </div>
      </div>

      <!-- Dual firm: Rajput | Rajdoot -->
      <div v-if="dualFirmMode" class="mt-4">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <!-- Rajput (left) -->
          <div class="overflow-hidden rounded-lg border border-slate-200">
            <div
              class="px-3 py-2 text-sm font-semibold tracking-wide text-slate-800 uppercase bg-slate-100 border-b border-slate-200"
            >
              Rajput
              <span class="ml-1 font-normal normal-case text-slate-500"
                >(sales / chalans)</span
              >
            </div>
            <div class="p-3">
              <div
                v-if="!rajputInvoice"
                class="p-4 text-sm text-center text-slate-500 bg-slate-50 rounded border border-dashed border-slate-200"
              >
                There are no chalans for this request, so there is no Rajput
                bill.
              </div>
              <firm-invoice-panel
                v-else
                :invoice="rajputInvoice"
                :payable-index="payableIndex(rajputInvoice)"
                :discount-amounts="discountAmounts"
                :invoice-payment-amounts="invoicePaymentAmounts"
                @update-discount="onPanelDiscount"
                @update-payment="onPanelPayment"
              />
            </div>
          </div>

          <!-- Rajdoot (right) -->
          <div class="overflow-hidden rounded-lg border border-slate-200">
            <div
              class="px-3 py-2 text-sm font-semibold tracking-wide text-slate-800 uppercase bg-slate-100 border-b border-slate-200"
            >
              Rajdoot
              <span class="ml-1 font-normal normal-case text-slate-500"
                >(labor payment)</span
              >
            </div>
            <div class="p-3">
              <div
                v-if="!rajdootInvoice"
                class="p-4 text-sm text-center text-slate-500 bg-slate-50 rounded border border-dashed border-slate-200"
              >
                Vendor / labor invoice not found for this request.
              </div>
              <firm-invoice-panel
                v-else
                :invoice="rajdootInvoice"
                :payable-index="payableIndex(rajdootInvoice)"
                :discount-amounts="discountAmounts"
                :invoice-payment-amounts="invoicePaymentAmounts"
                @update-discount="onPanelDiscount"
                @update-payment="onPanelPayment"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Standard Invoice List -->
      <div v-else class="mt-4 invoice-list">
        <h4 class="mb-2 font-bold">Invoice Details</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase border-b"
                >
                  Invoice No.
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase border-b"
                >
                  Date
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Invoice Amount
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Received
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Balance
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Discount
                </th>
                <th
                  class="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase border-b"
                >
                  Payment Amount
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(invoice, index) in selectedInvoices"
                :key="index"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-2 text-sm text-gray-900 border-b">
                  {{ invoice.identifier }}
                </td>
                <td class="px-4 py-2 text-sm text-gray-600 border-b">
                  {{ formatDate(invoice.date) }}
                </td>
                <td class="px-4 py-2 text-sm text-right text-gray-900 border-b">
                  {{ invoice.amount | currency }}
                </td>
                <td class="px-4 py-2 text-sm text-right text-gray-600 border-b">
                  {{ invoice.received | currency }}
                </td>
                <td
                  class="px-4 py-2 text-sm font-medium text-right border-b"
                  :class="
                    invoice.balance > 0 ? 'text-red-600' : 'text-green-600'
                  "
                >
                  {{ invoice.balance | currency }}
                </td>
                <td class="px-4 py-2 text-sm border-b">
                  <input
                    v-model.number="discountAmounts[index]"
                    type="number"
                    min="0"
                    :max="invoice.balance"
                    class="px-2 py-1 w-full text-right rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="
                      updateDiscount(index, $event.target.value, invoice.balance)
                    "
                    placeholder="0.00"
                  />
                </td>
                <td class="px-4 py-2 text-sm border-b">
                  <input
                    v-model.number="invoicePaymentAmounts[index]"
                    type="number"
                    min="0"
                    :max="getMaxPaymentAmount(index)"
                    class="px-2 py-1 w-full text-right rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="
                      updateInvoicePaymentAmount(index, $event.target.value)
                    "
                    :placeholder="getCalculatedPaymentAmount(index)"
                  />
                  <div class="mt-1 text-xs text-right text-gray-500">
                    Max: {{ getCalculatedPaymentAmount(index) | currency }}
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td colspan="5" class="px-4 py-2 text-sm font-bold text-right">
                  Total Discount:
                </td>
                <td
                  class="px-4 py-2 text-sm font-bold text-right text-orange-600"
                >
                  {{ totalDiscount | currency }}
                </td>
                <td></td>
              </tr>
              <tr>
                <td colspan="5" class="px-4 py-2 text-sm font-bold text-right">
                  Total Payment:
                </td>
                <td class="px-4 py-2 text-sm font-bold text-right text-green-600">
                  {{ totalInvoicePayment | currency }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Dual mode totals -->
      <div
        v-if="dualFirmMode && selectedInvoices.length > 0"
        class="grid grid-cols-2 gap-4 p-3 mt-3 text-sm bg-gray-50 rounded-lg border border-gray-200"
      >
        <div>
          <strong>Total Discount:</strong>
          <span class="ml-1 text-orange-600">{{ totalDiscount | currency }}</span>
        </div>
        <div class="text-right">
          <strong>Total Payment:</strong>
          <span class="ml-1 text-green-600">{{
            totalInvoicePayment | currency
          }}</span>
        </div>
      </div>

      <!-- Payment Modes (only when something is payable) -->
      <div v-if="selectedInvoices.length > 0" class="mt-4 payment-modes">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-bold">Payment Details</h4>
          <div class="flex gap-2">
            <button
              v-if="totalPaid > 0 && selectedInvoices.length > 1"
              @click="autoDistributePayment"
              class="px-3 py-1 text-xs text-white bg-purple-600 rounded hover:bg-purple-700"
              title="Distribute total payment amount proportionally across invoices"
            >
              Auto Distribute Payment
            </button>
            <button
              v-if="balance !== 0 && totalInvoicePayment > 0"
              @click="autoFillPaymentModes"
              class="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Auto Fill Payment Modes
            </button>
          </div>
        </div>
        <div class="flex flex-row gap-2 justify-between">
          <div
            v-for="paymentMode in sortedPaymentModes"
            :key="paymentMode.paymentModeId"
            class="flex flex-col p-2 justify-between min-w-[300px] bg-white rounded-lg border"
          >
            <div class="w-1/3 font-medium">
              <a @click="handlePaymentModeClick(paymentMode)">{{
                paymentMode.label
              }}</a>
            </div>
            <div class="w-full">
              <input
                :value="paymentAmounts[paymentMode.paymentModeId] || 0"
                @input="
                  updatePaymentAmount(
                    paymentMode.paymentModeId,
                    $event.target.value
                  )
                "
                type="number"
                min="0"
                class="w-full border border-gray-300 form-input"
                :placeholder="`${paymentMode.label} amount`"
              />
            </div>
          </div>
        </div>

        <!-- Total Paid and Balance -->
        <div class="p-4 mt-2 bg-gray-100 rounded-lg payment-summary">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <strong>Paying: </strong>
              <span class="text-green-600">{{ totalPaid | currency }}</span>
            </div>
            <div>
              <strong>Balance: </strong>
              <span :class="balanceClass">{{ balance | currency }}</span>
            </div>
          </div>
          <div
            v-if="balance > 0"
            class="p-2 mt-2 text-blue-800 bg-blue-100 rounded border border-blue-400"
          >
            <i class="mr-1 fas fa-info-circle"></i>
            <strong>Info:</strong> This is a partial payment. Remaining balance:
            {{ balance | currency }}. You can make additional payments later to
            clear the remaining balance.
          </div>
          <div
            v-if="balance < 0"
            class="p-2 mt-2 text-red-800 bg-red-100 rounded border border-red-400"
          >
            <i class="mr-1 fas fa-exclamation-triangle"></i>
            <strong>Error:</strong> Payment amount ({{ totalPaid | currency }})
            exceeds the total invoice payment ({{
              totalInvoicePayment | currency
            }}) by {{ Math.abs(balance) | currency }}. Please reduce payment
            mode amounts.
          </div>
          <div
            v-if="balance === 0 && totalInvoicePayment > 0"
            class="p-2 mt-2 text-green-800 bg-green-100 rounded border border-green-400"
          >
            <i class="mr-1 fas fa-check-circle"></i>
            <strong>Perfect!</strong> Payment breakdown matches the invoice
            payment amount.
          </div>
        </div>

        <!-- Payment Note -->
        <div class="mt-4 payment-note">
          <label class="block mb-1 font-medium">Payment Note</label>
          <textarea
            v-model="paymentNote"
            class="w-full border border-gray-300 rounded form-textarea"
            rows="3"
            placeholder="Enter any additional payment notes..."
          ></textarea>
        </div>
      </div>

      <div
        v-else-if="dualFirmMode"
        class="p-3 mt-4 text-sm text-green-800 bg-green-50 rounded border border-green-200"
      >
        Both sides are already settled for this service request. No payment is
        due.
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end space-x-2">
        <button
          @click="handleClose"
          class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="button"
          :disabled="
            saving ||
              balance < 0 ||
              totalPaid === 0 ||
              selectedInvoices.length === 0
          "
          @click="handleSave"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
        >
          <span v-if="saving" class="mr-2 fas fa-spinner fa-spin"></span>
          Save Payment
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/Modal.vue";
import FirmInvoicePanel from "@/components/FirmInvoicePanel.vue";

export default {
  name: "InvoicePaymentPopup",
  components: { Modal, FirmInvoicePanel },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedInvoices: {
      type: Array,
      default: () => []
    },
    customerName: {
      type: String,
      default: ""
    },
    firmName: {
      type: String,
      default: ""
    },
    saving: {
      type: Boolean,
      default: false
    },
    dualFirmMode: {
      type: Boolean,
      default: false
    },
    serviceRequestNumber: {
      type: String,
      default: ""
    },
    rajputInvoice: {
      type: Object,
      default: null
    },
    rajdootInvoice: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      paymentAmounts: {},
      invoicePaymentAmounts: [],
      discountAmounts: [],
      paymentNote: "",
      internalShow: this.show
    };
  },
  computed: {
    ...mapGetters({
      paymentModeList: "paymentMode/getPaymentModes"
    }),
    totalDiscount() {
      return this.discountAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    totalInvoicePayment() {
      return this.selectedInvoices.reduce((sum, invoice, index) => {
        const enteredAmount = parseFloat(this.invoicePaymentAmounts[index]) || 0;
        if (enteredAmount > 0) {
          return sum + enteredAmount;
        }
        const balance = parseFloat(invoice.balance) || 0;
        const discount = parseFloat(this.discountAmounts[index]) || 0;
        const paymentAmount = Math.max(0, balance - discount);
        return sum + paymentAmount;
      }, 0);
    },
    totalPaid() {
      return Object.values(this.paymentAmounts).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    balance() {
      return this.totalInvoicePayment - this.totalPaid;
    },
    balanceClass() {
      if (this.balance === 0) {
        return "text-green-600 font-bold";
      } else if (this.balance > 0) {
        return "text-orange-600 font-bold";
      } else {
        return "text-red-600 font-bold";
      }
    },
    sortedPaymentModes() {
      const order = ["UPI", "Cash", "Card", "Cheque", "Online"];
      const modes = Array.isArray(this.paymentModeList)
        ? this.paymentModeList
        : [];
      return [...modes].sort((a, b) => {
        const aIndex = order.indexOf(a.label);
        const bIndex = order.indexOf(b.label);

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        return a.label.localeCompare(b.label);
      });
    }
  },
  watch: {
    show(newVal) {
      this.internalShow = newVal;
      if (newVal) {
        this.initializePaymentAmounts();
        if (this.selectedInvoices && this.selectedInvoices.length > 0) {
          this.discountAmounts = this.selectedInvoices.map(() => 0);
          this.invoicePaymentAmounts = this.selectedInvoices.map(() => 0);
          this.recalculatePaymentAmounts();
        } else {
          this.discountAmounts = [];
          this.invoicePaymentAmounts = [];
        }
      }
    },
    internalShow(newVal) {
      if (!newVal) {
        this.$emit("update:show", false);
      }
    }
  },
  methods: {
    payableIndex(invoice) {
      if (!invoice) return -1;
      return this.selectedInvoices.findIndex(
        inv =>
          (inv.id && inv.id === invoice.id) ||
          (inv.identifier && inv.identifier === invoice.identifier)
      );
    },
    onPanelDiscount({ index, value, balance }) {
      this.updateDiscount(index, value, balance);
    },
    onPanelPayment({ index, value }) {
      this.updateInvoicePaymentAmount(index, value);
    },
    initializePaymentAmounts() {
      this.paymentAmounts = {};
      this.paymentNote = "";
      const modes = this.paymentModeList || [];
      modes.forEach(mode => {
        this.$set(this.paymentAmounts, mode.paymentModeId, 0);
      });
    },
    updatePaymentAmount(modeId, value) {
      this.$set(this.paymentAmounts, modeId, parseFloat(value) || 0);
    },
    updateDiscount(index, value, balance) {
      if (index < 0) return;
      const discount = parseFloat(value) || 0;
      const maxDiscount = parseFloat(balance) || 0;

      if (discount > maxDiscount) {
        this.$set(this.discountAmounts, index, maxDiscount);
        this.$notify({
          message: `Discount cannot exceed balance of ${this.$options.filters.currency(
            maxDiscount
          )}`,
          type: "warning",
          title: "Warning"
        });
      } else {
        this.$set(this.discountAmounts, index, discount);
      }

      this.recalculatePaymentAmount(index);
    },
    recalculatePaymentAmount(index) {
      if (index < 0) return;
      const invoice = this.selectedInvoices[index];
      if (!invoice) return;
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      const maxPaymentAmount = Math.max(0, balance - discount);

      if (
        !this.invoicePaymentAmounts[index] ||
        this.invoicePaymentAmounts[index] === 0
      ) {
        this.$set(this.invoicePaymentAmounts, index, maxPaymentAmount);
      } else {
        const currentAmount =
          parseFloat(this.invoicePaymentAmounts[index]) || 0;
        if (currentAmount > maxPaymentAmount) {
          this.$set(this.invoicePaymentAmounts, index, maxPaymentAmount);
        }
      }
    },
    updateInvoicePaymentAmount(index, value) {
      if (index < 0) return;
      const invoice = this.selectedInvoices[index];
      if (!invoice) return;
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      const maxAmount = Math.max(0, balance - discount);
      const enteredAmount = parseFloat(value) || 0;

      if (enteredAmount > maxAmount) {
        this.$set(this.invoicePaymentAmounts, index, maxAmount);
        this.$notify({
          message: `Payment amount cannot exceed balance (${this.$options.filters.currency(
            maxAmount
          )})`,
          type: "warning",
          title: "Warning"
        });
      } else {
        this.$set(this.invoicePaymentAmounts, index, enteredAmount);
      }
    },
    recalculatePaymentAmounts() {
      this.selectedInvoices.forEach((invoice, index) => {
        this.recalculatePaymentAmount(index);
      });
    },
    getMaxPaymentAmount(index) {
      const invoice = this.selectedInvoices[index];
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      return Math.max(0, balance - discount);
    },
    getCalculatedPaymentAmount(index) {
      const invoice = this.selectedInvoices[index];
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      return Math.max(0, balance - discount);
    },
    handleClose() {
      this.internalShow = false;
    },
    handleSave() {
      if (this.balance < 0) {
        this.$notify({
          message: "Payment amount cannot exceed total invoice payment",
          type: "danger",
          title: "Error"
        });
        return;
      }

      if (this.totalPaid === 0) {
        this.$notify({
          message: "Please enter payment amount in at least one payment mode",
          type: "warning",
          title: "Validation Error"
        });
        return;
      }

      const totalInvoicePayments = this.invoicePaymentAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
      const difference = Math.abs(this.totalPaid - totalInvoicePayments);

      if (difference > 0.01) {
        this.$notify({
          message: `Sum of invoice payments (${this.$options.filters.currency(
            totalInvoicePayments
          )}) must match total payment amount (${this.$options.filters.currency(
            this.totalPaid
          )}). Please adjust invoice payment amounts.`,
          type: "warning",
          title: "Validation Error"
        });
        return;
      }

      this.$emit("save", this.getPaymentData());
    },
    handlePaymentModeClick(paymentMode) {
      Object.keys(this.paymentAmounts).forEach(modeId => {
        this.$set(this.paymentAmounts, modeId, 0);
      });
      this.$set(
        this.paymentAmounts,
        paymentMode.paymentModeId,
        this.totalInvoicePayment
      );
    },
    autoFillPaymentModes() {
      Object.keys(this.paymentAmounts).forEach(modeId => {
        this.$set(this.paymentAmounts, modeId, 0);
      });

      if (this.sortedPaymentModes.length === 1) {
        this.$set(
          this.paymentAmounts,
          this.sortedPaymentModes[0].paymentModeId,
          this.totalInvoicePayment
        );
      } else if (this.sortedPaymentModes.length > 0) {
        this.$set(
          this.paymentAmounts,
          this.sortedPaymentModes[0].paymentModeId,
          this.totalInvoicePayment
        );
      }
    },
    autoDistributePayment() {
      const totalPaidAmount = this.totalPaid;

      if (totalPaidAmount <= 0) {
        this.$notify({
          message: "Please enter payment amount in payment modes first",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      const invoicePayableAmounts = this.selectedInvoices.map(
        (invoice, index) => {
          const balance = parseFloat(invoice.balance) || 0;
          const discount = parseFloat(this.discountAmounts[index]) || 0;
          return Math.max(0, balance - discount);
        }
      );

      const totalPayableAmount = invoicePayableAmounts.reduce(
        (sum, amount) => sum + amount,
        0
      );

      if (totalPayableAmount === 0) {
        this.$notify({
          message: "No payable amount available for distribution",
          type: "warning",
          title: "Warning"
        });
        return;
      }

      this.selectedInvoices.forEach((invoice, index) => {
        const payableAmount = invoicePayableAmounts[index];
        const proportion = payableAmount / totalPayableAmount;
        const distributedAmount = totalPaidAmount * proportion;
        const roundedAmount = Math.round(distributedAmount * 100) / 100;
        this.$set(
          this.invoicePaymentAmounts,
          index,
          Math.min(payableAmount, roundedAmount)
        );
      });

      const totalDistributed = this.invoicePaymentAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
      const remainder = totalPaidAmount - totalDistributed;

      if (Math.abs(remainder) > 0.01) {
        const firstInvoiceIndex = this.invoicePaymentAmounts.findIndex(
          amount => amount > 0
        );
        if (firstInvoiceIndex !== -1) {
          const currentAmount =
            parseFloat(this.invoicePaymentAmounts[firstInvoiceIndex]) || 0;
          const maxAmount = invoicePayableAmounts[firstInvoiceIndex];
          const newAmount = Math.min(
            maxAmount,
            Math.round((currentAmount + remainder) * 100) / 100
          );
          this.$set(this.invoicePaymentAmounts, firstInvoiceIndex, newAmount);
        }
      }

      this.$notify({
        message: `Payment of ${this.$options.filters.currency(
          totalPaidAmount
        )} distributed across ${this.selectedInvoices.length} invoice(s)`,
        type: "success",
        title: "Distribution Complete"
      });
    },
    getPaymentData() {
      const paymentBreakdown = Object.keys(this.paymentAmounts)
        .filter(modeId => this.paymentAmounts[modeId] > 0)
        .map(modeId => {
          const mode = this.paymentModeList.find(
            m => m.paymentModeId === modeId
          );
          return {
            paymentModeId: modeId,
            paymentModeLabel: mode ? mode.label : "",
            amount: parseFloat(this.paymentAmounts[modeId])
          };
        });

      const totalPaidAmount = this.totalPaid;
      const totalEnteredAmount = this.invoicePaymentAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );

      const invoicePayments = this.selectedInvoices.map((invoice, index) => {
        const balance = parseFloat(invoice.balance) || 0;
        const discount = parseFloat(this.discountAmounts[index]) || 0;
        const maxPayableAmount = Math.max(0, balance - discount);

        let actualPaymentAmount =
          parseFloat(this.invoicePaymentAmounts[index]) || 0;

        if (actualPaymentAmount === 0 && totalEnteredAmount === 0) {
          if (this.selectedInvoices.length === 1) {
            actualPaymentAmount = totalPaidAmount;
          } else {
            const totalPayable = this.selectedInvoices.reduce(
              (sum, inv, idx) => {
                const bal = parseFloat(inv.balance) || 0;
                const disc = parseFloat(this.discountAmounts[idx]) || 0;
                return sum + Math.max(0, bal - disc);
              },
              0
            );

            if (totalPayable > 0) {
              const proportion = maxPayableAmount / totalPayable;
              actualPaymentAmount = totalPaidAmount * proportion;
            }
          }
        }

        actualPaymentAmount = Math.min(
          maxPayableAmount,
          Math.round(actualPaymentAmount * 100) / 100
        );

        return {
          invoiceId: invoice.identifier,
          invoiceAmount: parseFloat(invoice.amount) || 0,
          id: invoice.id,
          paymentAmount: actualPaymentAmount,
          discount: discount,
          balance: balance,
          firm: invoice.firm || "",
          serviceRequestNumber: invoice.serviceRequestNumber || null
        };
      });

      const totalCalculatedPayment = invoicePayments.reduce(
        (sum, inv) => sum + inv.paymentAmount,
        0
      );
      const difference = totalPaidAmount - totalCalculatedPayment;

      if (Math.abs(difference) > 0.01 && invoicePayments.length > 0) {
        const firstInvoiceWithPayment = invoicePayments.findIndex(
          inv => inv.paymentAmount > 0
        );
        if (firstInvoiceWithPayment !== -1) {
          invoicePayments[firstInvoiceWithPayment].paymentAmount =
            Math.round(
              (invoicePayments[firstInvoiceWithPayment].paymentAmount +
                difference) *
                100
            ) / 100;
        }
      }

      return {
        totalPaid: this.totalPaid,
        totalInvoicePayment: this.totalInvoicePayment,
        balance: this.balance,
        paymentNote: this.paymentNote,
        paymentBreakdown,
        invoicePayments,
        selectedInvoices: this.selectedInvoices
      };
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
    }
  }
};
</script>

<style scoped>
.invoice-payment-popup {
  max-height: 70vh;
  overflow-y: auto;
}
</style>

<style>
.modal-xl {
  max-width: 1200px;
}
</style>
